import { useEffect, useState } from "react";
import { Heart, ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { ProductCard } from "../components/ProductCard";
import { useWishlist } from "../contexts/WishlistContext";
import { api } from "../lib/api";

export default function Wishlist() {
  const { ids, isLoading } = useWishlist();
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/products")
      .then((response) => setProducts(response.data))
      .catch(() => setError("Your saved products could not be loaded."));
  }, []);

  const savedProducts = products.filter((product) => ids.includes(product.id));

  return (
    <main className="mx-auto min-h-[70vh] max-w-7xl px-4 pb-28 pt-8 md:px-6 md:pb-12">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-orange-700">Saved for later</p>
      <h1 className="mt-2 font-serif text-4xl leading-none text-stone-950 md:text-5xl">Wishlist</h1>
      <p className="mt-3 text-sm text-stone-600">Your personal shortlist, ready whenever you are.</p>

      {error && (
        <p className="mt-8 border border-red-200 bg-red-50 p-4 text-sm text-red-800" data-testid="wishlist-error">
          {error}
        </p>
      )}

      {isLoading && (
        <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="wishlist-loading">
          {[1, 2, 3, 4].map((item) => (
            <div key={item} className="h-60 animate-pulse bg-stone-100" />
          ))}
        </div>
      )}

      {!isLoading && !error && savedProducts.length === 0 && (
        <section className="mt-8 grid min-h-72 place-items-center border border-dashed border-stone-300 bg-white p-8">
          <div className="max-w-sm text-center" data-testid="wishlist-empty">
            <Heart className="mx-auto h-9 w-9 text-orange-600" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-3xl text-stone-950">Nothing saved yet</h2>
            <p className="mt-2 text-sm leading-6 text-stone-600">
              Save products you love and find them here when you are ready to order.
            </p>
            <Link
              to="/categories"
              className={
                "mt-6 inline-flex min-h-11 items-center gap-2 bg-orange-600 px-5 text-sm " +
                "font-semibold text-white transition-colors hover:bg-orange-700"
              }
              data-testid="wishlist-start-shopping"
            >
              <ShoppingBag className="h-4 w-4" />
              Start shopping
            </Link>
          </div>
        </section>
      )}

      {!isLoading && savedProducts.length > 0 && (
        <section className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
          {savedProducts.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </section>
      )}
    </main>
  );
}